import { append } from '@akin-home/arrays';
import { HasChecklist, createChecklistItem } from '@akin-home/checklists';
import { pastWithinLast20Seconds } from '@akin-home/dates';
import { isAfter } from 'date-fns';
import {
  CalendarItem,
  CalendarItemType,
  CheckListItem,
  createAlarmModel,
  createAlertModel,
  createEventModel,
  createMealModel,
} from '../../models';
import { pipe } from '../../utils/composition';
import { endOfDay, getTomorrow, startOfDay } from '../../utils/dateTime';
import { sortByStartDate } from '../../utils/sorting';
import { resetMealTypeDateTimes } from '../meals/actions';
import { CalendarItemUnion } from '../types';

/**
 * Adds a checklist item to given record from given text
 */
export const addChecklistItemFromText = <T extends HasChecklist>(
  itemWithChecklist: T,
  text: string
): T =>
  addChecklistItem(itemWithChecklist, createChecklistItem({ title: text }));

/**
 * Adds a checklist item to given record
 */
export const addChecklistItem = <T extends HasChecklist>(
  itemWithChecklist: T,
  item: CheckListItem
): T => ({
  ...itemWithChecklist,
  checklist: append(itemWithChecklist.checklist, item),
});

// Converts calendar item from one type to another
export const convertItemType = (
  originalItem: CalendarItem,
  toType: CalendarItemType
) => {
  // If its the same type, just return the item
  if (originalItem.type === toType) {
    return originalItem;
  }

  return pipe(
    getBaseCalendarItemFields(toType),
    convertChecklist(originalItem),
    convertEndAndLocation(originalItem),
    doConvertCreate(toType)
  )(originalItem);
};

export const getBaseCalendarItemFields =
  (toType: CalendarItemType) => (item: CalendarItem) => ({
    id: item.id,
    habitatID: item.habitatID,
    title: item.title,
    type: toType,
    emoji: item.emoji,
    start: item.start,
    notes: item.notes,
    participants: item.participants,

    recurrenceExcludeDates: item.recurrenceExcludeDates,
    recurrenceInstanceData: item.recurrenceInstanceData,
    recurrence: item.recurrence,
    recurrenceUntil: item.recurrenceUntil,

    image: item.image,
    imageUrl: item.imageUrl,
    imageUpload: item.imageUpload,
    s3ImageUrl: item.s3ImageUrl,
    isRecurrence: item.isRecurrence,
  });

export const convertChecklist =
  (origin: CalendarItem) => (item: CalendarItem) => {
    if (origin.type === 'Meal' || item.type === 'Meal') {
      return item;
    } else {
      return {
        ...item,
        checklist: origin.checklist,
      };
    }
  };

export const convertEndAndLocation =
  (origin: CalendarItem) => (item: CalendarItem) => {
    if (
      ['Alert', 'Alarm'].includes(origin.type) ||
      ['Alert', 'Alarm'].includes(item.type)
    ) {
      return item;
    } else {
      return {
        ...item,
        end: origin.end,
        location: origin.location,
      };
    }
  };

export const doConvertCreate =
  (toType: CalendarItemType) =>
  (item: CalendarItem): CalendarItem => {
    switch (toType) {
      case 'Meal':
        return resetMealTypeDateTimes(createMealModel(item));
      case 'Event':
        return createEventModel(item);
      case 'Alert':
        return createAlertModel(item);
      case 'Alarm':
        return createAlarmModel(item);
      default:
        throw Error('Incompatible Calendar Item Type');
    }
  };

/** Gets the next 10 upcoming items */
export const getUpcoming = <T extends { start: Date }>(items: T[]): T[] =>
  items
    .filter((i) => i.start >= new Date())
    .sort(sortByStartDate)
    .slice(0, 10);

/** Gets the single next upcoming item */
export const getNextUpcoming = <T extends { start: Date }>(items: T[]): T =>
  getUpcoming(items)[0];

export const getUpcomingToday = <T extends { start: Date }>(
  items: T[]
): T[] => {
  const now = new Date();
  const endOfDay = new Date(now.getTime());
  endOfDay.setHours(23, 59, 59, 999);
  return getWithinDateRange(items, now, endOfDay);
};

export const getOnTime = <T extends { start: Date }>(
  items: T[],
  time: string,
  getDualisticTime = false
): T[] => {
  const hour = parseInt(time.split(':')[0]);
  const minute = parseInt(time.split(':')[1]);
  const timeAsDate = new Date();
  timeAsDate.setHours(hour, minute, 0, 0);

  const isDualisticTime = Number(hour) < 13;

  let returnedItems = items.filter(
    (i) =>
      i.start.getHours() === timeAsDate.getHours() &&
      i.start.getMinutes() === timeAsDate.getMinutes()
  );
  if (isDualisticTime && getDualisticTime) {
    const dualisticHour = hour === 12 ? 0 : hour + 12;
    const dualisticTimeItems = items.filter(
      (i) =>
        i.start.getHours() == dualisticHour &&
        i.start.getMinutes() == timeAsDate.getMinutes()
    );
    returnedItems = returnedItems.concat(dualisticTimeItems);
  }

  return returnedItems;
};

export const getWithinDateRange = <T extends { start: Date }>(
  items: T[],
  startDate: Date,
  endDate: Date
): T[] =>
  items
    .filter((i) => i.start >= startDate && i.start <= endDate)
    .sort(sortByStartDate);

export const getOnDate = <T extends { start: Date }>(
  items: T[],
  date: Date
): T[] => {
  const startOfDay = new Date(date.getTime());
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date.getTime());
  endOfDay.setHours(23, 59, 59, 999);
  return getWithinDateRange(items, startOfDay, endOfDay);
};

export const startDatePast = (item: { start: Date }) => past(item.start);

export const past = (date: Date) => date < new Date();

export { pastWithinLast20Seconds };

export const isStartingInNext24Hours = (item: { start: Date }): boolean =>
  item.start > new Date() && item.start < getTomorrow();

/**
 * As of writing this function calendar item coords can be stored in the notes as a
 * comma separated pair of floats. This is just for demo purposes at the moment.
 */
export const getCalendarItemCoords = (item: {
  notes?: string;
}): { lat: number; lng: number } | undefined => {
  if (!item.notes) return;

  const split = item.notes.split(',');

  if (split.length !== 2) return;

  try {
    return {
      lat: parseFloat(split[0]),
      lng: parseFloat(split[1]),
    };
  } catch (e) {
    return;
  }
};

export const getItemOrigin = <T extends CalendarItemUnion>(
  items: T[],
  recurringItem: T
): T | undefined => items.find((i) => recurringItem.origin?.id === i.id);

export const getDateFilter = (date: Date) => ({
  filter: {
    or: [
      {
        start: {
          between: [
            startOfDay(date).toISOString(),
            endOfDay(date).toISOString(),
          ],
        },
      },
      { recurrenceUntil: { gt: date.toISOString() } },
    ],
  },
});

export const calendarItemIsAfter = (afterDate: Date) => (i: CalendarItem) =>
  isAfter(i.start, afterDate);
